import { ArrowUpRight, MapPin, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { MaskedLine, FadeUp } from "@/components/motion";
import { STOCKISTS } from "@/data/stockists";
import { LIFESTYLE_IMG } from "@/data/products";

const Stockists = () => (
  <main className="mx-auto max-w-[1500px] px-6 pb-32 pt-32 md:px-10 md:pt-44" data-testid="stockists-page">
    <FadeUp>
      <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-ink/50">
        Where to find us
      </p>
    </FadeUp>
    <h1 className="mt-5 max-w-4xl font-display text-6xl font-medium leading-[1.02] tracking-tight text-ink md:text-8xl" data-testid="stockists-headline">
      <MaskedLine>Find a stockist</MaskedLine>
    </h1>
    <FadeUp delay={0.3} className="mt-8 max-w-xl">
      <p className="text-base leading-relaxed text-ink/70 md:text-lg">
        Experience the finish in person — visit a SanCub partner showroom near you.
      </p>
    </FadeUp>

    <div className="mt-20 space-y-16" data-testid="stockists-list">
      {STOCKISTS.map((s, si) => (
        <div key={s.state}>
          <FadeUp>
            <h2 className="flex items-baseline gap-4 font-display text-3xl font-medium tracking-tight text-ink md:text-4xl" data-testid={`state-${s.state}`}>
              {s.state}
              <span className="text-sm font-normal text-ink/40">
                {s.stores.length > 0
                  ? `${s.stores.length} ${s.stores.length === 1 ? "showroom" : "showrooms"}`
                  : "Coming soon"}
              </span>
            </h2>
          </FadeUp>
          {s.stores.length === 0 && (
            <FadeUp className="mt-7">
              <div
                className="flex items-center justify-center rounded-[2rem] border border-dashed border-ink/15 bg-white/50 py-16"
                data-testid={`stockist-coming-soon-${s.state.toLowerCase()}`}
              >
                <p className="font-display text-2xl font-medium tracking-tight text-ink/35">Coming soon</p>
              </div>
            </FadeUp>
          )}
          <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {s.stores.map((store, i) => (
              <FadeUp key={store.name} delay={i * 0.06}>
                <div
                  className="group rounded-[2rem] bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition duration-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)]"
                  data-testid={`stockist-${s.state.toLowerCase()}-${i}`}
                >
                  <p className="font-display text-xl font-medium tracking-tight text-ink">{store.name}</p>
                  <p className="mt-3 flex items-center gap-2 text-sm text-ink/60">
                    <MapPin className="h-4 w-4 text-ink/40" />
                    {store.suburb}
                  </p>
                  <p className="mt-1.5 flex items-center gap-2 text-sm text-ink/60">
                    <Phone className="h-4 w-4 text-ink/40" />
                    {store.phone}
                  </p>
                  <a
                    href={store.mapsUrl || `https://www.google.com/maps/search/${encodeURIComponent(store.name + " " + store.suburb)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`stockist-directions-${s.state.toLowerCase()}-${i}`}
                    className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-ink transition-colors duration-300 hover:text-ink/50"
                  >
                    Get directions
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                </div>
              </FadeUp>
            ))}
          </div>
          {s.stores.some((st) => st.coords) && (
            <FadeUp className="mt-7">
              <div className="overflow-hidden rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`stockist-map-${s.state.toLowerCase()}`}>
                <iframe
                  title={`${s.state} stockist map`}
                  src={`https://www.google.com/maps?q=${s.stores.find((st) => st.coords).coords}&z=16&output=embed`}
                  className="h-[380px] w-full border-0 md:h-[440px]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </FadeUp>
          )}
          {si < STOCKISTS.length - 1 && <div className="mt-16 h-px bg-ink/5" />}
        </div>
      ))}
    </div>

    <FadeUp className="mt-24">
      <div className="relative overflow-hidden rounded-[3rem]" data-testid="become-stockist-banner">
        <img src={LIFESTYLE_IMG} alt="SanCub showroom bathroom display" className="h-[380px] w-full object-cover md:h-[440px]" />
        <div className="absolute inset-0 bg-black/35" aria-hidden="true" />
        <div className="absolute inset-0 flex flex-col items-start justify-center p-10 md:p-16">
          <h2 className="max-w-lg font-display text-4xl font-medium leading-[1.1] tracking-tight text-white md:text-5xl">
            Become a SanCub stockist
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-white/80">
            Join a growing network of premium showrooms. Trade pricing, display support
            and marketing assets included.
          </p>
          <Link
            to="/contact"
            data-testid="become-stockist-cta"
            className="group mt-8 flex items-center gap-2 rounded-full bg-white px-8 py-4 text-[14px] font-semibold text-ink transition duration-300 hover:scale-[1.03] hover:bg-white/90"
          >
            Apply now
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>
    </FadeUp>
  </main>
);

export default Stockists;
